import {FormikProps} from "formik";
import React, {FC, memo, useEffect, useState} from "react";
import {Button, Card, Descriptions, Divider, Modal, Space, Table, message} from "antd";
import {ColumnProps, TableProps} from "antd/lib/table";
import {grey, yellow} from "@ant-design/colors";
import {ApolloError} from "@apollo/client";
import {Link, useLocation} from "react-router-dom";
import {PossibleFormikPropsType} from "./CourseCreateForm";
import Roles from "../../../../shared/models/roles";
import {
    GetBookingsByCourseDocument,
    GetCanceledBookingsDocument,
    GetCourseByIdDocument,
    GetCourseLessonsByCourseIdDocument,
    GetDetailedCourseTypesDocument,
    GetInstructorsDocument,
    Rate,
    ResetAllUnsetteledScheduledPaymentsOfCourseMutation,
    useResetAllUnsetteledScheduledPaymentsOfCourseMutation,
} from "../../../generated/graphql";
import {getBookingDetailsPath} from "../../../helpers/bookingDetailsRouting";
import {compareDecimalDigits} from "../../../helpers/compareDecimalDigits";
import useHighestRole from "../../../helpers/useHighestRole";
import {useCurrency} from "../../../helpers/currencyHelper";
import {renderAmount, CurrencyShort} from "../../../helpers/renderAmount";
import i18n from "../../../services/i18n";
import css from "./ResetScheduledPaymentsForCourse.less";

type ResetScheduledPaymentsForCourseProps = {
    formikProps: FormikProps<PossibleFormikPropsType>;
    courseId?: string;
};

/**
 * Zeigt einen Fehlerdialog für eine fehlgeschlagene Reset-Mutation. Der
 * Spezialfall "keine Buchungen" erhält einen eigenen Hinweistext; alle anderen
 * Server-Fehler (z.B. der fail-closed Guard, wenn bereits Raten abgerechnet
 * wurden) werden mit ihrer bereits enduser-tauglichen Server-Message angezeigt.
 */
const showResetError = (error: ApolloError | Error): void => {
    const content = error.message.includes("No bookings found for this course")
        ? "Für diesen Kurs wurden keine Buchungen gefunden."
        : error.message;

    Modal.error({
        title: i18n.containers.common.ErrorHandler.modalTitle(),
        content: <div>{content}</div>,
    });
};

const ResetScheduledPaymentsForCourse: FC<ResetScheduledPaymentsForCourseProps> = ({formikProps, courseId}) => {
    const currency = useCurrency();
    const [previewData, setPreviewData] = useState<
        ResetAllUnsetteledScheduledPaymentsOfCourseMutation["resetAllUnsetteledScheduledPaymentsOfCourse"] | undefined
    >(undefined);

    const [
        resetScheduledPayments,
        {data: resetScheduledPaymentsDataPreview},
    ] = useResetAllUnsetteledScheduledPaymentsOfCourseMutation({
        variables: {courseId: courseId as string, preview: true},
    });

    useEffect(() => {
        const fetchedResetScheduledPaymentsDataPreview:
            | ResetAllUnsetteledScheduledPaymentsOfCourseMutation["resetAllUnsetteledScheduledPaymentsOfCourse"]
            | undefined = resetScheduledPaymentsDataPreview?.resetAllUnsetteledScheduledPaymentsOfCourse;

        if (fetchedResetScheduledPaymentsDataPreview) {
            setPreviewData(fetchedResetScheduledPaymentsDataPreview);
        }
    }, [resetScheduledPaymentsDataPreview]);

    // HANDLE HIGHEST ROLE --------------------------------------------------------------------------------------------------------
    const highestRole = useHighestRole();
    const location = useLocation();
    // Nur nicht abgerechnete Buchungen werden zurückgesetzt; Einzelbuchungs-Kurse öffnen den EZB-Editor (#1233).
    const bookingDetailsPath = (bookingId: string) => getBookingDetailsPath(bookingId, false, Boolean(formikProps.values.allowLessonBooking), highestRole, location.pathname);

    if (highestRole !== Roles.Superuser) return null;

    // if (!previewData) return null;

    // ----------------------------------------------------------------------------------------------------------------------------

    return (
        <>
            <Modal
                width={1000}
                // style={{top: 20, height: "90%", overflow: "auto"}}
                // style={{height: "90%"}}
                centered
                title="Vorschau Ratenberechnung"
                open={previewData !== undefined}
                onCancel={() => setPreviewData(undefined)}
                onOk={() => setPreviewData(undefined)}
                // footer={[<div key="1">{"Footer"}</div>]}
                okText="Jetzt zurücksetzen"
                cancelText="Ansicht Schließen"
                okButtonProps={{
                    type: "primary",
                    onClick: async () => {
                        if (courseId) {
                            resetScheduledPayments({
                                variables: {
                                    courseId,
                                    preview: false,
                                },
                                refetchQueries: [
                                    "GetCoursesWithPagination",
                                    "GetInstructors",
                                    {
                                        query: GetInstructorsDocument,
                                    },
                                    "GetDetailedCourseTypes",
                                    {
                                        query: GetDetailedCourseTypesDocument,
                                    },
                                    "GetCourseLessonsByCourseId",
                                    {
                                        query: GetCourseLessonsByCourseIdDocument,
                                        variables: {
                                            courseId,
                                        },
                                    },
                                    "GetBookingsByCourse",
                                    {
                                        query: GetBookingsByCourseDocument,
                                        variables: {
                                            id: courseId,
                                        },
                                    },
                                    "getCourseById",
                                    {
                                        query: GetCourseByIdDocument,
                                        variables: {
                                            id: courseId,
                                        },
                                    },
                                    "GetUnsettledBookings",
                                    "GetSettledBookings",
                                    "GetCanceledBookings",
                                    {query: GetCanceledBookingsDocument},
                                ],
                            })
                                .then((res) => {
                                    const bookingCount =
                                        res.data?.resetAllUnsetteledScheduledPaymentsOfCourse?.bookings?.length;

                                    message.success({
                                        type: "success",
                                        title: "Erfolg",
                                        content: `Die Raten von ${bookingCount} Buchungen wurden zurückgesetzt.`,
                                    });
                                })
                                // Server-Guard (z.B. bereits abgerechnete Raten) blockt den
                                // Reset — Fehler sichtbar machen statt still zu verschlucken.
                                .catch(showResetError);
                        }
                    },
                }}
            >
                {previewData && renderScheduledPaymentsPreview(previewData, currency.short as CurrencyShort, bookingDetailsPath)}
            </Modal>
            <Button
                size="small"
                danger
                onClick={() => {
                    resetScheduledPayments().catch(showResetError);
                }}
                block
            >
                {"Raten aller Buchungen des Kurses zurücksetzen"}
            </Button>
        </>
    );
};

export default memo(ResetScheduledPaymentsForCourse);

const renderScheduledPaymentsPreview = (
    previewData: ResetAllUnsetteledScheduledPaymentsOfCourseMutation["resetAllUnsetteledScheduledPaymentsOfCourse"],
    currencyShort: CurrencyShort,
    bookingDetailsPath: (bookingId: string) => string,
) => {
    const {courseId, coursePrice, courseDueDates} = previewData;
    const money = (amount: number): string => renderAmount({amount, currency: currencyShort, short: true});

    const tableOptions: TableProps<Rate> = {
        pagination: false,
        size: "small",
    };

    const courseRateTableData: Array<Rate> = courseDueDates.map((courseDueDate) => {
        return {
            key: courseDueDate.key,
            rate: courseDueDate,
            value: {
                due: courseDueDate.value.due,
                amount: courseDueDate.value.amount,
            },
        };
    });

    const columns: Array<ColumnProps<Rate>> = [
        {
            title: "Fälligkeit",
            dataIndex: "rate",
            key: "due",
            render: (rate: Rate) => {
                return rate.value.due;
            },
        },
        {
            title: "Betrag",
            dataIndex: "rate",
            key: "amount",
            render: (rate: Rate) => {
                return money(Number(rate.value.amount));
            },
        },
    ];

    const columnsBookings: Array<ColumnProps<Rate>> = [
        {
            title: "Fälligkeit",
            dataIndex: "rate",
            key: "due",
            render: (rate: Rate, _, index) => {
                // Eine Buchung kann mehr Raten haben als der Kursplan (eigene Zahlweise der Buchung).
                const courseRate = courseDueDates[index];
                const differs = !courseRate?.value.due || rate.value.due !== courseRate.value.due;

                if (differs) {
                    return <div style={{background: yellow[2]}}>{rate.value.due}</div>;
                }

                return rate.value.due;
            },
        },
        {
            title: "Betrag",
            dataIndex: "rate",
            key: "amount",
            render: (rate: Rate, _, index) => {
                const courseRate = courseDueDates[index];
                const isSame = courseRate !== undefined && (!courseRate.value.due || compareDecimalDigits(Number(rate.value.amount), Number(courseRate.value.amount)));

                if (!isSame) {
                    return <div style={{background: yellow[2]}}>{money(Number(rate.value.amount))}</div>;
                }

                return money(Number(rate.value.amount));
            },
        },
    ];

    const CourseDetails = () => {
        return (
            <div
                style={{
                    flex: 1,
                    display: "flex",
                    paddingRight: 10,
                    paddingLeft: 10,
                }}
            >
                <Descriptions
                    style={{flex: 2}}
                    title={<div>{courseId}</div>}
                    size="small"
                    column={1}
                    bordered
                    // labelStyle={{fontSize: "1em"}}
                    // contentStyle={{fontSize: "1em"}}
                >
                    <Descriptions.Item label="Kurs ID">{courseId}</Descriptions.Item>
                    <Descriptions.Item label="Preis" contentStyle={{fontSize: "1em", fontWeight: "bold"}}>
                        {money(coursePrice)}
                    </Descriptions.Item>
                </Descriptions>
                <Divider type="vertical" />
                <Divider type="vertical" />
                <Table<Rate>
                    title={() => "Raten des Kurses"}
                    style={{flex: 2}}
                    columns={columns}
                    dataSource={courseRateTableData}
                    {...tableOptions}
                />
            </div>
        );
    };

    const BookingDetails = ({
        booking,
    }: {
        booking: ResetAllUnsetteledScheduledPaymentsOfCourseMutation["resetAllUnsetteledScheduledPaymentsOfCourse"]["bookings"][number];
    }) => {
        const {
            bookingId,
            bookingPrice,
            creditedBalance,
            currentScheduledPaymentsCount,
            newScheduledPaymentsCount,
            currentScheduledPaymentsAmountsAndDueDates,
            newScheduledPaymentsAmountsAndDueDates,
        } = booking;

        const bookingCurrentRateTableData: Array<Rate> = currentScheduledPaymentsAmountsAndDueDates.map(
            (bookingDueDate) => {
                return {
                    key: bookingDueDate.key,
                    rate: bookingDueDate,
                    value: {
                        due: bookingDueDate.value.due,
                        amount: bookingDueDate.value.amount,
                    },
                };
            },
        );

        const bookingNewRateTableData: Array<Rate> = newScheduledPaymentsAmountsAndDueDates.map((bookingDueDate) => {
            return {
                key: bookingDueDate.key,
                rate: bookingDueDate,
                value: {
                    due: bookingDueDate.value.due,
                    amount: bookingDueDate.value.amount,
                },
            };
        });

        const coursePriceDiffers = bookingPrice.toFixed(2) !== coursePrice.toFixed(2);

        return (
            <div style={{display: "flex"}}>
                <Descriptions
                    style={{flex: 2}}
                    // title={<div style={{fontSize: "1em"}}>{bookingNumber}</div>}
                    size="small"
                    column={1}
                    bordered
                    labelStyle={{fontSize: "0.8em"}}
                    contentStyle={{fontSize: "0.8em", fontWeight: "bold"}}
                >
                    <Descriptions.Item label="ID" style={{fontSize: "0.7em"}}>
                        {bookingId}
                    </Descriptions.Item>

                    <Descriptions.Item label="Verrechnetes Guthaben">{creditedBalance ?? "-"}</Descriptions.Item>
                    <Descriptions.Item label="Bisherige Raten">{currentScheduledPaymentsCount}</Descriptions.Item>
                    <Descriptions.Item label="Neue Raten">{newScheduledPaymentsCount}</Descriptions.Item>
                    <Descriptions.Item label="Preis" contentStyle={{fontSize: "1em", fontWeight: "bold"}}>
                        {coursePriceDiffers ? (
                            <div style={{background: yellow[2]}}>{money(bookingPrice)}</div>
                        ) : (
                            <div>{money(bookingPrice)}</div>
                        )}
                    </Descriptions.Item>
                </Descriptions>
                <Divider type="vertical" />
                <Table<Rate>
                    style={{flex: 1}}
                    title={() => "Bisherige Raten"}
                    columns={columns}
                    dataSource={bookingCurrentRateTableData}
                    {...tableOptions}
                />
                <Divider type="vertical" />
                <Table<Rate>
                    style={{flex: 1}}
                    title={() => "Zukünftige Raten"}
                    columns={columnsBookings}
                    dataSource={bookingNewRateTableData}
                    {...tableOptions}
                />
            </div>
        );
    };

    return (
        <Card
            className={css.card}
            cover={
                <Card
                    className={css.coverCard}
                    style={{borderBottom: `3px solid ${grey[1]}`}}
                    title="Einstellungen des Kurses"
                >
                    <CourseDetails />
                </Card>
            }
        >
            <div className={css.scrollable}>
                {previewData.bookings.map((booking, index) => {
                    const detailsUrl = bookingDetailsPath(booking.bookingId);

                    return (
                        <Card
                            key={booking.bookingId}
                            style={{marginBottom: 5}}
                            title={booking.bookingNumber}
                            extra={
                                <Space>
                                    <Link to={detailsUrl} target="_blank">
                                        <Button type="link">{"Zur Buchung"}</Button>
                                    </Link>
                                    <div>{index + 1}</div>
                                </Space>
                            }
                        >
                            <BookingDetails booking={booking} />
                        </Card>
                    );
                })}
            </div>
        </Card>
    );
};
